import React, { useState, useEffect } from "react";
import { Button, Input, Skeleton, Tooltip, Dropdown, AutoComplete } from "antd";
import {
  CloseOutlined,
  MoreOutlined,
  CheckOutlined,
  CloseOutlined as CloseIcon,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import LightningIcon from "../../../../assets/customSVG/LightningIcon";
import {
  inboxCreateQuickReply,
  inboxUpdateQuickReply,
  inboxDeleteQuickReply,
} from "../../api/Api";
import {
  useAspDispatch,
  useAspSelector,
} from "../../../../test/jest-redux-hooks";
import { updateToggleToast } from "../../../../reduxToolkit/appSlice";

export default function QuickReplyPanel({
  quickReplies = [],
  setQuickReplies,
  loading,
  setShowQuickRepliesManager,
  currentBrand,
  selectedMessage,
}) {
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [editingReply, setEditingReply] = useState(null);
  const [editMessageText, setEditMessageText] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]); // array of reply ids being deleted
  const MAX_MESSAGE_LENGTH = 1000;
  const { toggleToast } = useAspSelector((state) => state.app);
  const dispatch = useAspDispatch();

  // Initialize selection
  useEffect(() => {
    if (quickReplies.length > 0 && selectedGroupId === null) {
      setSelectedGroupId("all");
    }
  }, [quickReplies, selectedGroupId]);

  // Count total replies across all groups
  const totalReplies = quickReplies.reduce(
    (sum, g) => sum + g.replies.length,
    0
  );
  const isAll = selectedGroupId === "all";

  // Find single group if not 'all'
  const selectedGroup = quickReplies.find((g) => g.id === selectedGroupId);

  const handleDeleteMessage = async (groupId, replyId) => {
    if (deletingIds.includes(replyId)) return;
    setDeletingIds((ids) => [...ids, replyId]);
    try {
      const params = {
        id: groupId,
        brand_id: currentBrand.brand_id,
        quick_reply_id: replyId,
      };
      const response = await inboxDeleteQuickReply(params);
      if (response?.status === 200) {
        const updatedQuickReplies = quickReplies.map((group) =>
          group.id === groupId
            ? {
                ...group,
                replies: group.replies.filter((r) => r.id !== replyId),
              }
            : group
        );
        setQuickReplies(updatedQuickReplies);
        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content: "Quick reply deleted successfully",
              status: "Success",
              duration: "",
            },
          ])
        );
      } else {
        throw new Error("Failed to delete quick reply");
      }
    } catch (error) {
      console.error("Error deleting message:", error);
      dispatch(
        updateToggleToast([
          ...toggleToast,
          {
            id: toggleToast?.length + 1,
            content: "Failed to delete quick reply",
            status: "Failed",
            duration: "",
          },
        ])
      );
    } finally {
      setDeletingIds((ids) => ids.filter((id) => id !== replyId));
    }
  };


  const handleEditMessage = async () => {
    if (!editingReply || !editMessageText.trim() || isEditing) return;
    setIsEditing(true);
    try {
      const params = {
        id: editingReply.groupId,
      };
      const bodyParams = {
        quick_reply_id: editingReply.replyId,
        brand_id: currentBrand.brand_id,
        message: editMessageText,
      };
      const response = await inboxUpdateQuickReply(params, bodyParams);
      if (
        response?.status === 200 &&
        response?.data?.message === "Quick reply updated successfully"
      ) {
        // Create a new array with the updated message
        const updatedQuickReplies = quickReplies.map((group) => {
          if (group.id === editingReply.groupId) {
            return {
              ...group,
              replies: group.replies.map((r) =>
                r.id === editingReply.replyId
                  ? { ...r, message: editMessageText }
                  : r
              ),
            };
          }
          return group;
        });

        // Update the state with the new array
        if (typeof setQuickReplies === "function") {
          setQuickReplies(updatedQuickReplies);
          setEditingReply(null);
          setEditMessageText("");
          dispatch(
            updateToggleToast([
              ...toggleToast,
              {
                id: toggleToast?.length + 1,
                content: "Quick reply updated successfully",
                status: "Success",
                duration: "",
              },
            ])
          );
        } else {
          throw new Error("setQuickReplies is not a function");
        }
      } else {
        throw new Error("Failed to update quick reply");
      }
    } catch (error) {
      console.error("Error editing message:", error);
      dispatch(
        updateToggleToast([
          ...toggleToast,
          {
            id: toggleToast?.length + 1,
            content: "Failed to update quick reply",
            status: "Failed",
            duration: "",
          },
        ])
      );
    } finally {
      setIsEditing(false);
    }
  };


  const handleAddQuickReply = async () => {
    if (!newTitle || !newMessage.trim() || isAdding) return;
    setIsAdding(true);
    try {
      const bodyParams = {
        brand_id: currentBrand.brand_id,
        title: newTitle,
        message: newMessage.trim(),
      };

      const response = await inboxCreateQuickReply(bodyParams);

      if (response?.status === 200) {
        // Update the quickReplies state with the new reply
        const updatedQuickReplies = [...quickReplies];
        const existingGroup = updatedQuickReplies.find(
          (g) => g.title === newTitle
        );

        if (existingGroup) {
          // Add to existing group
          existingGroup.replies.push({
            id: response.data.quick_reply.quick_replies[response.data.quick_reply.quick_replies.length - 1].id,
            message: newMessage.trim(),
            status: 10,
          });
        } else {
          // Create new group
          updatedQuickReplies.push({
            id: response.data.quick_reply.id,
            title: newTitle,
            replies: [
              {
                id: response.data.quick_reply.quick_replies[0].id,
                message: newMessage.trim(),
                status: 10,
              },
            ],
          });
        }

        setQuickReplies(updatedQuickReplies);
        setNewTitle("");
        setNewMessage("");
        setShowForm(false);

        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content: "Quick reply created successfully",
              status: "Success",
              duration: "",
            },
          ])
        );
      } else {
        throw new Error(response?.response?.data?.message);
      }
    } catch (error) {
      console.error("Error creating quick reply:", error);
      dispatch(
        updateToggleToast([
          ...toggleToast,
          {
            id: toggleToast?.length + 1,
            content: `Failed to create quick reply: ${error}`,
            status: "Failed",
            duration: "",
          },
        ])
      );
    } finally {
      setIsAdding(false);
    }
  };


  // Prepare all replies flattened with group label
  const allReplies = quickReplies.flatMap((g) =>
    g.replies.map((r) => ({ ...r, groupTitle: g.title, groupId: g.id }))
  );

  const getReplyMenuItems = (groupId, replyId, message) => [
    {
      key: "edit",
      label: (
        <span className="text-xs flex items-center gap-2">
          <EditOutlined className="text-xs" />
          Edit
        </span>
      ),
      onClick: () => {
        setEditingReply({ groupId, replyId, message });
        setEditMessageText(message);
      },
      disabled: isEditing,
    },
    {
      key: "delete",
      label: (
        <span className="text-xs flex items-center gap-2">
          <DeleteOutlined className="text-xs" />
          {deletingIds.includes(replyId) ? "Deleting..." : "Delete"}
        </span>
      ),
      onClick: () => !deletingIds.includes(replyId) && handleDeleteMessage(groupId, replyId),
      disabled: deletingIds.includes(replyId),
    },
  ];

  const handleMessageClick = (message) => {
    selectedMessage(message);
    setShowQuickRepliesManager(false);
  };

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center h-full text-center p-4">
      <div className="text-gray-500 mb-4">No quick replies found</div>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setShowForm(true)}
        className="bg-[#04815d]"
      >
        Add Quick Reply
      </Button>
    </div>
  );

  const renderAddQuickReplyForm = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-xs text-gray-600 mb-1">Group</label>
        <AutoComplete
          className="w-full"
          placeholder="Select existing group or type to create new"
          value={newTitle}
          onChange={(value) => {
            setNewTitle(value);
            setDropdownVisible(!!value);
          }}
          onSelect={(value) => {
            setNewTitle(value);
            setDropdownVisible(false);
          }}
          onBlur={() => setDropdownVisible(false)}
          options={quickReplies.map((group) => ({
            value: group.title,
            label: group.title,
          }))}
          style={{ width: "100%" }}
          defaultActiveFirstOption={false}
          showSearch
          defaultOpen={false}
          open={dropdownVisible}
          filterOption={(inputValue, option) =>
            option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
          }
        />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">Message</label>
        <Input.TextArea
          rows={3}
          placeholder="Type your message here..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          className="text-xs"
          style={{ fontSize: "12px" }}
        />
      </div>
      <div className="flex space-x-2">
        <button
          onClick={handleAddQuickReply}
          className={`bg-[#04815d] text-white px-4 py-2 rounded transition-opacity  ${
            !newTitle || !newMessage.trim() || isAdding
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-[#04815d] hover:opacity-90"
          }`}
          disabled={!newTitle || !newMessage.trim() || isAdding}
        >
          {isAdding ? "Adding..." : "Add Quick Reply"}
        </button>

        <button
          onClick={() => setShowForm(false)}
          className="px-4 py-2 rounded hover:bg-gray-100 border border-[#e5e7eb] transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderReplyItem = (reply, groupId) => {
    if (
      editingReply?.replyId === reply.id &&
      editingReply?.groupId === groupId
    ) {
      return (
        <div className="flex items-center gap-2 w-full text-xs">
          <div className="flex-1 relative">
            <Input.TextArea
              value={editMessageText}
              onChange={(e) => setEditMessageText(e.target.value)}
              autoSize={{ minRows: 1, maxRows: 3 }}
              className="text-xs text-[#575b5e]"
              style={{ fontSize: "12px" }}
            />
          </div>
          <div className="flex gap-3">
            <CheckOutlined
              className={`text-xs cursor-pointer ${
                editMessageText.trim() && !isEditing ? "text-green-500" : "text-gray-300"
              } ${isEditing ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={editMessageText.trim() && !isEditing ? handleEditMessage : undefined}
              disabled={isEditing}
            />
            <CloseIcon
              className="text-xs text-red-500 cursor-pointer"
              onClick={() => {
                setEditingReply(null);
                setEditMessageText("");
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-between items-center w-full gap-1">
        <div
          className={`flex-1 cursor-pointer truncate ${
            selectedMessage === reply.message ? "text-[#04815d]" : ""
          }`}
          onClick={() => handleMessageClick(reply.message)}
        >
          <Tooltip title={reply.message}>
            <span className="truncate w-[80%] pr-2 inline-block text-xs">
              {reply.message}
            </span>
          </Tooltip>
        </div>
        <Dropdown
          menu={{ items: getReplyMenuItems(groupId, reply.id, reply.message) }}
          trigger={["click"]}
        >
          <MoreOutlined className="cursor-pointer text-xs" />
        </Dropdown>
      </div>
    );
  };

  const renderGroupEmptyState = (group) => (
    <div className="flex flex-col items-center justify-center h-full text-center p-4">
      <div className="text-gray-500 mb-4">No replies in {group.title}</div>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          setSelectedGroupId(group.id);
          setShowForm(true);
        }}
        className="bg-[#04815d]"
      >
        Add Reply to {group.title}
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col text-xs justify-between border rounded-md max-w-3xl mx-auto mt-10 shadow-lg p-3.5 min-w-[50vw]">
      {/* Header */}
      <div className="flex justify-between pb-3.5 border-b border-[#e5e7eb]">
        <div className="flex items-center text-[rgba(4,129,93,255)] bg-[rgba(217,252,210,255)] rounded-md">
          <button
            disabled={isAll}
            onClick={() => setShowForm(true)}
            className="flex items-center gap-[0.5vw] px-2 py-[8px] cursor-pointer font-semibold hover:bg-[rgba(217,252,210,255)]"
          >
            <LightningIcon svgClassName="size-[1vw]" fill="currentColor" />
            Quick Reply
          </button>
        </div>
        <div className="flex items-center">
          <div
            className="flex items-center gap-[0.5vw] cursor-pointer border-r border-[#e5e7eb] font-semibold px-[1vw] text-[rgba(129,95,243,255)]"
            onClick={() => setShowForm(true)}
          >
            <div className="text-[18px] text-center">+</div>
            <div>New Reply</div>
          </div>
          <div className="text-[#7b818b] px-[1vw] cursor-pointer font-light">
            <button
              onClick={() => {
                setShowQuickRepliesManager(false);
                setShowForm(false);
              }}
            >
              <CloseOutlined style={{ fontSize: 14 }} />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-row">
        {/* Sidebar: All + Groups */}
        <div className="border-r pt-2 pr-2 min-w-[12vw] text-[#575b5e] h-[30vh] overflow-y-auto">
          {loading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <ul>
              {/* All section */}
              <li
                key="all"
                onClick={() => setSelectedGroupId("all")}
                className={`flex justify-between items-center p-2 cursor-pointer hover:bg-gray-100 rounded ${
                  isAll ? "text-[#1e8a6a] font-semibold" : ""
                }`}
              >
                <span># All</span>
                <span>{totalReplies}</span>
              </li>

              {/* Individual groups */}
              {quickReplies.map((group) => (
                <li
                  key={group.id}
                  onClick={() => {
                    setShowForm(false)
                    setSelectedGroupId(group.id)
                  }}
                  className={`flex justify-between items-center p-2 cursor-pointer hover:bg-gray-100 rounded gap-1 ${
                    selectedGroupId === group.id
                      ? "text-[#1e8a6a] font-semibold"
                      : ""
                  }`}
                >
                  <Tooltip title={group.title} placement="topLeft">
                    <span className="max-w-[16vw] truncate">
                    {" "}
                      <span className="pr-1"> # </span>
                      {group.title}
                    </span>
                  </Tooltip>
                  <span>{group.replies.length}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Main panel: Replies list or form */}
        <div className="p-4 flex-1 text-[#575b5e] h-[30vh] overflow-y-auto max-w-[48vw]">
          {loading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : showForm ? (
            renderAddQuickReplyForm()
          ) : quickReplies.length === 0 ? (
            renderEmptyState()
          ) : isAll ? (
            <ul className="space-y-2 w-full">
              {allReplies.map(({ id, message, groupTitle, groupId }) => (
                <li
                  key={`${groupId}-${id}`}
                  className="flex justify-between items-center border-b-[1.5px] border-dashed border-[#e5e7eb] pb-[10px] pt-[6px] flex justify-between items-center w-fullw-[100%] truncate"
                >
                  {renderReplyItem({ id, message }, groupId)}
                </li>
              ))}
            </ul>
          ) : selectedGroup?.replies.length === 0 ? (
            renderGroupEmptyState(selectedGroup)
          ) : (
            <ul className="space-y-2 w-full">
              {selectedGroup?.replies.map((reply) => (
                <li
                  key={reply.id}
                  className="flex justify-between items-center border-b-[1.5px] border-dashed border-[#e5e7eb] pb-[10px] pt-[6px] max-w-[100%] truncate"
                >
                  {renderReplyItem(reply, selectedGroup.id)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
